import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { apiClient } from '@babomrs/api-client';
import { useClientAuth } from './useClientAuth';
import { initActiveEntity, isEntityRefused, setActiveEntity } from '../lib/activeEntity';

export interface PortalEntityOption { clientRef: string; displayName: string; entityType: string; isOwn: boolean }
export interface ActiveEntity extends PortalEntityOption { pendingQueries: number }

interface ActiveEntityContextValue {
  options: PortalEntityOption[];
  active: ActiveEntity | null;
  /** Resolves true once the switch is done; false when it was refused, failed, or another switch was pending. */
  switchTo: (clientRef: string) => Promise<boolean>;
  isSwitching: boolean;
}

const ActiveEntityContext = createContext<ActiveEntityContextValue>({
  options: [], active: null, switchTo: async () => false, isSwitching: false,
});

const ENTITIES_KEY = ['portal-entities'] as const;

/**
 * Cached `['client', …]` reads about the PERSON, which never change with the switch (spec §7). Everything else
 * under `['client', …]` belongs to the active entity and is dropped on a switch. `session-policy` in particular
 * must survive: dropping it would restart the inactivity timer (Story #22576). The Sign page's requests
 * (`signing-requests`, `signing-request/{id}`) are the person's across every entity (§4.9), and so are the
 * people whose mobiles they verify (`mobile-verification`).
 */
const PERSON_SCOPED = new Set([
  'session-policy', 'comms-preferences', 'onboarding', 'signing-requests', 'signing-request', 'mobile-verification',
]);

function isEntityScoped(key: QueryKey): boolean {
  return key[0] === 'client' && !PERSON_SCOPED.has(String(key[1]));
}

/**
 * Clear every cached read of the previous entity, so nothing of it is shown under the new one's name, and refetch
 * the ones on screen. A reset (not a remove) so a mounted page is told: a removed query leaves its observer holding
 * the old result until something re-renders it.
 */
function dropEntityScoped(queryClient: QueryClient): void {
  void queryClient.resetQueries({ predicate: (q) => isEntityScoped(q.queryKey) });
}

export function useActiveEntity(): ActiveEntityContextValue {
  return useContext(ActiveEntityContext);
}

/**
 * Spec §7 — holds the active entity for the signed-in portal. Mounted inside `PortalLoader`, so `user` is known.
 *
 * A remembered entity that is no longer granted (revoked since the last visit, or mid-session) is refused by the
 * server with the portal-entity-refused 403. The portal then falls back to the person's own record, drops the
 * previous entity's cached reads and refetches — it never shows an error page and never loops: once on the own
 * record no header is sent, and a request for the own record cannot be refused for its entity.
 */
export function ActiveEntityProvider({ children }: { children: ReactNode }) {
  const { user } = useClientAuth();
  const ownRef = user?.clientRef ?? null;
  const queryClient = useQueryClient();
  const [activeRef, setActiveRef] = useState<string | null>(() => (ownRef ? initActiveEntity(ownRef) : null));

  // A different person in the same page (the context changed under us): restore THEIR choice, never the last one's.
  useEffect(() => {
    if (ownRef) setActiveRef(initActiveEntity(ownRef));
  }, [ownRef]);

  const optionsQuery = useQuery<PortalEntityOption[]>({
    queryKey: [...ENTITIES_KEY, 'options'],
    queryFn: () => apiClient.get('/api/v1/client/entities'),
    enabled: !!ownRef,
  });

  const activeQuery = useQuery<ActiveEntity>({
    queryKey: [...ENTITIES_KEY, 'active', activeRef],
    queryFn: () => apiClient.get('/api/v1/client/entities/active'),
    enabled: !!ownRef && !!activeRef,
    retry: false,
  });

  const backToOwn = useCallback(() => {
    if (!ownRef) return;
    setActiveEntity(ownRef);
    setActiveRef(ownRef);
    dropEntityScoped(queryClient);
    void queryClient.invalidateQueries({ queryKey: [...ENTITIES_KEY, 'options'] });
  }, [ownRef, queryClient]);

  // The remembered entity cannot be confirmed — refused, no longer listed, or its read failed for any other
  // reason: fall back to the own record. Never leave pages scoped by the header to an entity the switcher and the
  // banner cannot name, which is what a failed active read would otherwise do (both render nothing without it).
  useEffect(() => {
    if (!ownRef || !activeRef || activeRef === ownRef) return;
    const notListed = optionsQuery.data !== undefined && !optionsQuery.data.some((o) => o.clientRef === activeRef);
    if (notListed || activeQuery.isError) backToOwn();
  }, [ownRef, activeRef, optionsQuery.data, activeQuery.isError, backToOwn]);

  // Any OTHER request refused for its entity (a page read after a mid-session revoke): re-check the active entity
  // and the list rather than reset on the spot, so a late refusal of a request sent before a switch cannot throw
  // the person off the entity they have just chosen. The re-check above does the reset if it is still refused.
  useEffect(() => {
    const recheck = () => { void queryClient.invalidateQueries({ queryKey: ENTITIES_KEY }); };
    const unsubscribeQueries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'error') return;
      if (event.query.queryKey[0] === ENTITIES_KEY[0]) return;
      if (isEntityRefused(event.action.error)) recheck();
    });
    const unsubscribeMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'error') return;
      if (isEntityRefused(event.action.error)) recheck();
    });
    return () => { unsubscribeQueries(); unsubscribeMutations(); };
  }, [queryClient]);

  const switchMutation = useMutation({
    mutationFn: (clientRef: string) => apiClient.post<ActiveEntity>('/api/v1/client/entities/active', { clientRef }),
    onSuccess: (entity) => {
      setActiveEntity(entity.clientRef);
      // Every entity-scoped page reads under ['client', ...]; drop them so nothing from the previous entity
      // is shown under the new one's name.
      dropEntityScoped(queryClient);
      queryClient.setQueryData([...ENTITIES_KEY, 'active', entity.clientRef], entity);
      setActiveRef(entity.clientRef);
    },
    // A refused target means the list is stale (its grant was revoked): refresh it. The current entity stays.
    onError: () => { void queryClient.invalidateQueries({ queryKey: [...ENTITIES_KEY, 'options'] }); },
  });

  const { mutateAsync, isPending } = switchMutation;
  const switchTo = useCallback(async (clientRef: string) => {
    // One switch at a time: further requests while one is pending are ignored, not queued.
    if (isPending) return false;
    try {
      await mutateAsync(clientRef);
      return true;
    } catch {
      // Handled in onError (the list is refreshed); the switcher goes back to showing the current entity.
      return false;
    }
  }, [mutateAsync, isPending]);

  const value = useMemo<ActiveEntityContextValue>(() => ({
    options: optionsQuery.data ?? [],
    active: activeQuery.data ?? null,
    switchTo,
    isSwitching: isPending,
  }), [optionsQuery.data, activeQuery.data, switchTo, isPending]);

  return <ActiveEntityContext.Provider value={value}>{children}</ActiveEntityContext.Provider>;
}
